import { DaysInfo, TimeRange } from '../types';
import { convertToApiTime, formatTimeForInput } from './time';

/**
 * Parses days_info from the API response into form-friendly format.
 * Returns a record keyed by weekday string ("1"–"7") with array of { start_time, end_time } in HH:mm.
 *
 * API format (DaysInfo): slots is Record<string, string[][] | null>
 *   e.g. { "1": [["01:00:00","03:00:00"], ["05:00:00","10:23:00"]], "2": null, ... }
 *
 * Form format: Record<string, TimeRange[] | null>
 *   e.g. { "1": [{start_time:"01:00",end_time:"03:00"}, {start_time:"05:00",end_time:"10:23"}], "2": null, ... }
 */
export const parseDaysInfoFromApi = (
  days_info?: DaysInfo
): Record<string, TimeRange[] | null> => {
  if (!days_info?.slots) return {};

  const parsed: Record<string, TimeRange[] | null> = {};
  for (const [key, value] of Object.entries(days_info.slots)) {
    if (!/^[1-7]$/.test(key)) {
      console.warn(`[parseDaysInfoFromApi] Unexpected weekday key: "${key}"`);
      continue;
    }
    if (value === null || value === undefined) {
      parsed[key] = null;
    } else if (Array.isArray(value)) {
      const ranges: TimeRange[] = [];
      for (const item of value) {
        if (item && Array.isArray(item) && item.length >= 2) {
          ranges.push({
            start_time: formatTimeForInput(item[0]),
            end_time: formatTimeForInput(item[1]),
          });
        }
      }
      parsed[key] = ranges.length > 0 ? ranges : null;
    }
  }
  return parsed;
};

/**
 * Converts form-state days_info (TimeRange arrays) back to the API payload shape.
 *
 * Form format: Record<string, TimeRange[] | null>
 * API format (DaysInfo): Record<string, string[][] | null>
 */
export const buildDaysInfoPayload = (
  overrides: Record<string, TimeRange[] | null>,
  activeDays: number[],
): DaysInfo => {
  const slots: Record<string, string[][] | null> = {};

  activeDays.forEach(day => {
    const key = String(day);
    const value = overrides[key];

    if (value && value.length > 0){
      slots[key] = value.map((range) => [
        convertToApiTime(range.start_time),
        convertToApiTime(range.end_time),
      ]);
    }
  });

  return { slots };
};
